// Sign-in for VERA Studio. Three interchangeable clients:
//  - desktop:   inside VERA.exe, Microsoft sign-in runs in the main process (system browser + PKCE).
//  - microsoft: in a browser, Microsoft sign-in with redirect (MSAL).
//  - dev:       nothing configured; pick a sample person ("View as"). Never used when Microsoft is configured.

import { config, microsoftConfigured } from "../config/env";
import { desktop } from "../lib/desktop";
import { PEOPLE } from "../data/sample";
import { installedApp } from "../lib/homeScreen";

export type StudioUser = { id: string; name: string; email: string; roles: string[]; samplePersonId?: string };

export interface AuthClient {
  kind: "desktop" | "microsoft" | "dev";
  /** Restores an existing session, or returns null. */
  init(): Promise<StudioUser | null>;
  signIn(personId?: string): Promise<StudioUser | null>;
  signOut(): Promise<void>;
  /** Access token for the VeraControlPlane API (null in dev). */
  getToken(): Promise<string | null>;
  /** Web only: a Microsoft Graph token for these scopes, as the signed-in person. `interactive` may show Microsoft's consent window. */
  graphToken?(scopes: string[], interactive: boolean): Promise<string>;
}

function safeGet(k: string) { try { return localStorage.getItem(k); } catch { return null; } }
function safeSet(k: string, v: string | null) { try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* ignore */ } }

export function devUser(personId: string): StudioUser | null {
  const p = PEOPLE.find((x) => x.id === personId);
  return p ? { id: p.id, name: p.name, email: p.email, roles: p.roles, samplePersonId: p.id } : null;
}

const devAuth: AuthClient = {
  kind: "dev",
  async init() { if (notSetUp) return null; const id = safeGet("vera-dev-person"); return id ? devUser(id) : null; },
  async signIn(personId = "eva") { if (notSetUp) throw new Error("This copy of VERA isn't set up for sign-in yet."); safeSet("vera-dev-person", personId); return devUser(personId); },
  async signOut() { safeSet("vera-dev-person", null); },
  async getToken() { return null; },
};

/** True while running with sample people instead of real Microsoft accounts. */
// On the web, only a local development server or an explicit demo build (VITE_DEMO=1) may use sample people.
const webDemoAllowed = import.meta.env.DEV || import.meta.env.VITE_DEMO === "1";
export let devMode = !desktop && !microsoftConfigured && webDemoAllowed;
/** True when this copy has neither Microsoft sign-in nor demo mode: nobody can get in. */
export let notSetUp = !desktop && !microsoftConfigured && !webDemoAllowed;

const desktopAuth = (): AuthClient => ({
  kind: "desktop",
  async init() {
    const configured = await desktop!.auth.configured();
    devMode = !configured && (await desktop!.demo());
    notSetUp = !configured && !devMode;
    if (notSetUp) return null;
    if (devMode) { const u = await devAuth.init(); await desktop!.folders.useProfile(u?.samplePersonId ?? null); return u; }
    const a = await desktop!.auth.getAccount();
    return a ? { id: a.homeAccountId, name: a.name, email: a.username, roles: a.roles } : null;
  },
  async signIn(personId) {
    if (notSetUp) throw new Error("This copy of VERA isn't set up for sign-in yet.");
    if (!(await desktop!.auth.configured())) { const u = await devAuth.signIn(personId); await desktop!.folders.useProfile(u?.samplePersonId ?? null); return u; }
    const a = await desktop!.auth.signIn();
    return { id: a.homeAccountId, name: a.name, email: a.username, roles: a.roles };
  },
  async signOut() { if (await desktop!.auth.configured()) await desktop!.auth.signOut(); else { await devAuth.signOut(); await desktop!.folders.useProfile(null); } },
  async getToken() { return (await desktop!.auth.configured()) ? desktop!.auth.getToken() : null; },
});

/** Where Microsoft sends the web app back after sign-in: "signin" next to the app (for example https://studio.veraneural.com/signin). */
export function webRedirectUri(loc: { origin: string; pathname: string } = window.location) {
  return `${loc.origin}${loc.pathname.replace(/[^/]*$/, "")}signin`;
}

function msalAuth(): AuthClient {
  // Loaded on demand so the sample build stays small.
  let pcaPromise: Promise<import("@azure/msal-browser").PublicClientApplication> | null = null;
  const scopes = ["openid", "profile", ...(config.apiScope ? [config.apiScope] : [])];
  const getPca = () =>
    (pcaPromise ??= import("@azure/msal-browser").then(async ({ PublicClientApplication }) => {
      const pca = new PublicClientApplication({
        auth: {
          clientId: config.clientId,
          authority: `https://login.microsoftonline.com/${config.tenantId}`,
          // Its own return address: VERA.exe's sign-in uses http://localhost, and Microsoft treats every localhost port as that one.
          redirectUri: webRedirectUri(),
        },
        // A browser tab forgets the sign-in when it closes; Studio added to the home screen remembers it (installedApp).
        cache: { cacheLocation: installedApp() ? "localStorage" : "sessionStorage" },
      });
      await pca.initialize();
      return pca;
    }));
  const toUser = (acc: import("@azure/msal-browser").AccountInfo): StudioUser => ({
    id: acc.homeAccountId,
    name: acc.name || acc.username,
    email: acc.username,
    roles: ((acc.idTokenClaims as { roles?: string[] } | undefined)?.roles) ?? [],
  });
  return {
    kind: "microsoft",
    async init() {
      const pca = await getPca();
      const res = await pca.handleRedirectPromise();
      // Back from Microsoft: show the app's own address again instead of /signin.
      if (/\/signin$/.test(window.location.pathname)) window.history.replaceState(null, "", window.location.pathname.replace(/signin$/, ""));
      const acc = res?.account ?? pca.getAllAccounts()[0];
      if (!acc) return null;
      pca.setActiveAccount(acc);
      return toUser(acc);
    },
    // Always let the person choose the account: otherwise Microsoft silently reuses whichever account the browser is
    // already signed in with (an EduTask test account opened EduTask with no way to pick another).
    async signIn() { const pca = await getPca(); await pca.loginRedirect({ scopes, prompt: "select_account" }); return null; },
    async signOut() { const pca = await getPca(); await pca.logoutRedirect(); },
    async getToken() {
      if (!config.apiScope) return null;
      const pca = await getPca();
      const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0];
      if (!account) return null;
      try {
        return (await pca.acquireTokenSilent({ scopes: [config.apiScope], account })).accessToken;
      } catch {
        await pca.acquireTokenRedirect({ scopes: [config.apiScope], account });
        return null;
      }
    },
    async graphToken(scopes, interactive) {
      const pca = await getPca();
      const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0];
      if (!account) throw new Error("Please sign in again.");
      try {
        return (await pca.acquireTokenSilent({ scopes, account })).accessToken;
      } catch (e) {
        if (!interactive) throw e;
        // A small Microsoft window, so the conversation on this page stays where it is.
        try { return (await pca.acquireTokenPopup({ scopes, account })).accessToken; }
        catch (p) {
          const m = p instanceof Error ? p.message : "";
          if (/popup_window_error|empty_window_error|popup.*block/i.test(m)) throw new Error("Your browser blocked Microsoft's sign-in window. Please allow pop-ups for VERA and try again.");
          if (/user_cancelled/i.test(m)) throw new Error("Cancelled.");
          if (/AADSTS65001|consent/i.test(m)) throw new Error("Microsoft needs permission for this first. Ask your administrator to approve VERA Studio.");
          throw new Error("Microsoft didn't connect. Please try again.");
        }
      }
    },
  };
}

export const auth: AuthClient = desktop ? desktopAuth() : microsoftConfigured ? msalAuth() : devAuth;
